import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/leuteliaApi";
import ConfrontoOTA from "./ConfrontoOTA";
import { useDeep, deepAction, deepStatus, DeepPages, DeepAdvice } from "./DeepAnalysis";
import ReportInline from "./ReportInline";
import { useReport } from "./ReportViewer";
import { Button } from "@/components/ui/button";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Cell, LabelList, Legend,
} from "recharts";
import {
  Star, MessageSquareText, Loader2, Copy, Check, AlertTriangle, Sparkles, Quote, Link2, Filter,
} from "lucide-react";
import { TT } from "./ChartKit";

const Overline = ({ children }) => <div className="text-brand-orange font-bold tracking-[0.22em] text-[11px] uppercase">{children}</div>;
const Card = ({ className = "", children, ...p }) => (
  <div className={`bg-white rounded-xl border border-brand-gold/25 shadow-[0_1px_2px_rgba(15,58,45,0.04)] ${className}`} {...p}>{children}</div>
);
const cap = (s) => s.charAt(0).toUpperCase() + s.slice(1);

function InboxHint({ scope }) {
  const [m, setM] = useState(null);
  useEffect(() => { api.inboxMine(scope.tenant, scope.hotel).then(setM).catch(() => setM(null)); }, [scope.tenant, scope.hotel]);
  if (!m?.configured || !m.address) return null;
  return (
    <p className="mt-3 text-[13px] text-brand-ink/85 rounded-lg border border-brand-gold/25 bg-brand-cream/50 px-3 py-2" data-testid="reviews-inbox-hint">
      <strong>Recensioni Booking ed Expedia:</strong> inoltra le email «nuova recensione» a <a href={`mailto:${m.address}`} className="text-brand-green font-semibold underline" data-testid="reviews-inbox-address">{m.address}</a> (anche con una regola automatica della tua posta): LEUTELIA le legge ogni ora e le porta qui.
      {m.count > 0 && <span className="ml-1 text-brand-muted" data-testid="reviews-inbox-count">Arrivate via email finora: {m.count}.</span>}
    </p>
  );
}


export default function Recensioni({ ccu, scope }) {
  const [data, setData] = useState(null);
  const [drafts, setDrafts] = useState({});
  const [busy, setBusy] = useState({});
  const [filter, setFilter] = useState("neg");

  const load = useCallback(async () => {
    const d = await api.reviews(scope.tenant, scope.hotel);
    setData(d);
    setDrafts(Object.fromEntries(d.drafts.map((x) => [x.target, x])));
  }, [scope.tenant, scope.hotel]);
  useEffect(() => { load().catch((e) => toast.error(e.message)); }, [load]);

  const markAnswered = async (reviewId, text, platform) => {
    try { await api.markAnswered({ tenant: scope.tenant, hotel: scope.hotel, review_id: reviewId, text }); toast.success(`Segnata come risposta su ${platform}.`); await load(); }
    catch (e) { toast.error(e.message); }
  };
  const publish = async (reviewId, text) => {
    setBusy((b) => ({ ...b, [`pub-${reviewId}`]: true }));
    try {
      await api.publishReply({ tenant: scope.tenant, hotel: scope.hotel, review_id: reviewId, comment: text });
      toast.success("Risposta pubblicata su Google"); await load();
    } catch (e) { toast.error(e.message); } finally { setBusy((b) => ({ ...b, [`pub-${reviewId}`]: false })); }
  };

  const { data: rep, generate, reload: reloadReport } = useReport("recensioni", scope, ccu.understanding_version);
  const deep = useDeep("recensioni", scope, () => { load(); reloadReport?.(); });
  const [job, setJob] = useState(null);
  useEffect(() => { api.respondAllStatus(scope.tenant, scope.hotel).then(setJob).catch(() => {}); }, [scope.tenant, scope.hotel]);
  const respondAll = async () => {
    try {
      const r = await api.respondAll({ tenant: scope.tenant, hotel: scope.hotel });
      if (r.started) { toast.success("LEUTELIA sta scrivendo una risposta per ogni recensione senza risposta: qualche minuto."); setJob({ status: "running", done: 0, total: 0 }); setTimeout(async () => { try { setJob(await api.respondAllStatus(scope.tenant, scope.hotel)); } catch { /* */ } }, 4000); }
      else toast.message("Scrittura già in corso");
    } catch (e) { toast.error(e.message); }
  };
  useEffect(() => { if (job?.status === "running") { const id = setInterval(async () => { try { const j = await api.respondAllStatus(scope.tenant, scope.hotel); setJob(j); if (j.status !== "running") { clearInterval(id); load(); } } catch { /* */ } }, 5000); return () => clearInterval(id); } }, [job?.status, scope.tenant, scope.hotel, load]);

  const propose = async (target, body) => {
    setBusy((b) => ({ ...b, [target]: true }));
    try {
      const d = await api.proposeReply({ tenant: scope.tenant, hotel: scope.hotel, ...body });
      setDrafts((x) => ({ ...x, [target]: d }));
      toast.success("Bozza di risposta pronta");
    } catch (e) { toast.error(e.message); } finally { setBusy((b) => ({ ...b, [target]: false })); }
  };

  if (!data) return (
    <div className="flex items-center gap-3 justify-center h-[60vh] text-brand-muted">
      <Loader2 className="animate-spin text-brand-gold" /> <span className="font-mono text-[13px]">analisi recensioni…</span>
    </div>
  );

  const s = data.summary;
  const ctx = data.ccu_context;
  const list = data.reviews.filter((r) => filter === "all" ? true : filter === "neg" ? r.sentiment === "neg" : (r.sentiment === "neg" && !r.responded));

  return (
    <div className="relative z-10 px-4 sm:px-6 lg:px-8 py-5 lg:py-7 max-w-[1320px] 2xl:max-w-[1560px] min-[2000px]:max-w-[1760px] leu-in" data-testid="reviews-module">
      <Overline>Recensioni</Overline>
      <div className="flex items-start gap-4 mt-3 mb-6">
        <div className="w-12 h-12 rounded-full bg-brand-green flex items-center justify-center shrink-0"><Star size={22} className="text-brand-gold" strokeWidth={1.75} /></div>
        <div>
          <h2 className="font-serif text-[26px] sm:text-[30px] lg:text-[34px] leading-[1.1] text-brand-ink">Cosa dicono gli ospiti e come rispondere.</h2>
          <p className="text-[14.5px] text-brand-muted mt-2 max-w-2xl">
            Analisi che parte da ciò che LEUTELIA sa (tono, punti di forza, criticità, strategia) di <strong>{ctx.name}</strong> e propone risposte pronte a recensioni e critiche ricorrenti.
          </p>
          <div className="flex flex-wrap gap-2 mt-3 text-[12px] font-mono">
            <span className="px-2.5 py-1 rounded-full bg-brand-green text-brand-cream font-semibold">basata sulla comprensione v{data.understanding_version}</span>
            <span data-testid="reviews-source-badge" className={`px-2.5 py-1 rounded-full border ${data.source === "mocked" ? "border-brand-gold/40 text-brand-muted" : "border-emerald-300 text-emerald-700 bg-emerald-50"}`}>
              {data.source === "gbp" ? "Google Business Profile · LIVE" : data.source === "live" ? `${(data.sources || []).join(" + ")} · LIVE` : data.source === "google_places" ? `${(data.sources || ["Google"]).join(" + ")} · LIVE` : "nessuna recensione reale letta"}
            </span>
            {data.source_error && <span className="px-2.5 py-1 rounded-full border border-brand-orange/40 text-brand-orange">GBP: {data.source_error}</span>}
          </div>
          {(data.source === "none" || data.source === "mocked") && <p className="mt-3 text-[13px] text-brand-orange" data-testid="reviews-none-notice">LEUTELIA non inventa recensioni: i testi arrivano da Google Business Profile (collega l'account dalla pagina Google), dalle OTA tramite il fornitore dati o dalle email di Booking/Expedia inoltrate. Finché non sono letti, qui non compaiono numeri.</p>}
          <InboxHint scope={scope} />
          <div className="mt-6"><ConfrontoOTA scope={scope} compact /></div>
          <div className="hidden">
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Kpi label="Voto medio" value={s.avg_rating.toFixed(2)} sub="su 5" testid="kpi-avg" />
        <Kpi label="Recensioni" value={s.count} sub="ultimi 4 mesi" testid="kpi-count" />
        <Kpi label="Positive" value={`${s.positive_share}%`} sub="sentiment" testid="kpi-positive" />
        <Kpi label="Negative senza risposta" value={s.unanswered_negative} sub="da gestire" accent testid="kpi-unanswered" />
      </div>

      <Card className="p-5 mb-6 flex flex-wrap gap-x-8 gap-y-3 items-start" data-testid="ccu-context-strip">
        <div className="flex items-center gap-2 text-brand-green font-semibold text-[13px]"><Link2 size={16} className="text-brand-gold" /> Cosa sappiamo dell'hotel</div>
        <Ctx label="Tono desiderato" value={ctx.desired_tone || ctx.tone_of_voice} />
        <Ctx label="Strategia" value={ctx.strategic_summary} />
        <Ctx label="Punti di forza" value={ctx.strengths.slice(0, 2).join(" · ")} />
        <Ctx label="Criticità note" value={ctx.criticalities.slice(0, 2).join(" · ")} />
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-8">
        <Card className="p-5">
          <ChartHead title="Distribuzione dei voti" sub="Numero di recensioni per stella" />
          <ResponsiveContainer width="100%" height={190}>
            <BarChart data={s.by_rating} margin={{ top: 16, left: -20, right: 8 }}>
              <XAxis dataKey="rating" tickFormatter={(v) => `${v}★`} tick={{ fontSize: 11, fill: "#6F6A5F" }} axisLine={false} tickLine={false} />
              <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: "#6F6A5F" }} axisLine={false} tickLine={false} />
              <Tooltip {...TT} cursor={{ fill: "#F5F1E8" }} />
              <Bar dataKey="count" radius={[6, 6, 0, 0]} barSize={28}>
                {s.by_rating.map((e, i) => <Cell key={i} fill={["#B0452E", "#C2662E", "#C6902A", "#7BA890", "#16493A"][i]} />)}
                <LabelList dataKey="count" position="top" style={{ fontSize: 11, fill: "#211F1A" }} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Card>
        <Card className="p-5">
          <ChartHead title="Per piattaforma" sub="Voto medio per canale" />
          <ResponsiveContainer width="100%" height={190}>
            <BarChart data={s.by_platform} layout="vertical" margin={{ left: 8, right: 36 }}>
              <XAxis type="number" domain={[0, 5]} hide />
              <YAxis type="category" dataKey="platform" width={90} tick={{ fontSize: 11, fill: "#6F6A5F" }} axisLine={false} tickLine={false} />
              <Tooltip {...TT} cursor={{ fill: "#F5F1E8" }} formatter={(v, n) => [v, n === "avg" ? "voto medio" : n]} />
              <Bar dataKey="avg" fill="#C6902A" radius={[0, 6, 6, 0]} barSize={16}>
                <LabelList dataKey="avg" position="right" formatter={(v) => `${v} (${s.by_platform.find((p) => p.avg === v)?.count ?? ""})`} style={{ fontSize: 11, fill: "#211F1A" }} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Card>
        <Card className="p-5">
          <ChartHead title="Temi ricorrenti" sub="Menzioni positive vs negative" />
          <ResponsiveContainer width="100%" height={190}>
            <BarChart data={s.themes.slice(0, 6).map((t) => ({ ...t, theme: cap(t.theme) }))} layout="vertical" margin={{ left: 8, right: 12 }}>
              <XAxis type="number" hide allowDecimals={false} />
              <YAxis type="category" dataKey="theme" width={80} tick={{ fontSize: 11, fill: "#6F6A5F" }} axisLine={false} tickLine={false} />
              <Tooltip {...TT} cursor={{ fill: "#F5F1E8" }} />
              <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="pos" name="positive" stackId="a" fill="#16493A" barSize={14} />
              <Bar dataKey="neg" name="negative" stackId="a" fill="#B0452E" radius={[0, 6, 6, 0]} barSize={14} />
            </BarChart>
          </ResponsiveContainer>
        </Card>
      </div>

      <section className="mb-8" data-testid="critiques-section">
        <div className="flex items-center gap-2.5 mb-3">
          <AlertTriangle size={20} className="text-brand-gold" strokeWidth={1.9} />
          <div>
            <h3 className="font-serif text-[20px] text-brand-ink leading-none">Critiche ricorrenti</h3>
            <p className="text-[12.5px] text-brand-muted mt-1">Temi negativi che si ripetono, collegati alle criticità già note. Genera una risposta pubblica standard da riutilizzare.</p>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {data.critiques.slice(0, 6).map((c) => {
            const target = `theme:${c.theme}`;
            const draft = drafts[target];
            return (
              <Card key={c.theme} className="p-5 flex flex-col" data-testid={`critique-${c.theme}`}>
                <div className="flex items-center justify-between">
                  <div className="font-serif text-[19px] text-brand-ink">{cap(c.theme)}</div>
                  <span className="px-2 py-0.5 rounded-full bg-red-100 text-red-700 border border-red-300 text-[10.5px] font-bold">{c.count} negative</span>
                </div>
                {c.linked_criticality && (
                  <div className="mt-2 text-[12px] text-brand-green flex items-start gap-1.5"><Link2 size={13} className="mt-0.5 shrink-0 text-brand-gold" /> Già segnalato: {c.linked_criticality}</div>
                )}
                <div className="mt-2 text-[12.5px] text-brand-muted leading-relaxed"><strong className="text-brand-ink">Azione:</strong> {c.suggested_action}</div>
                <div className="mt-auto pt-3">
                  {draft ? <Draft draft={draft} /> : (
                    <Button data-testid={`critique-reply-btn-${c.theme}`} disabled={busy[target]} onClick={() => propose(target, { theme: c.theme })}
                      variant="outline" className="h-9 rounded-lg border-brand-green/30 text-brand-green hover:bg-brand-green hover:text-brand-cream text-[12.5px] font-semibold">
                      {busy[target] ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Sparkles size={14} className="mr-2" />} Risposta standard
                    </Button>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      </section>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-8" data-testid="reviews-deep"><DeepActionCard deep={deep} /><DeepStatusCard deep={deep} /></div>
      <DeepAdvice module="recensioni" st={deep.st} scope={scope} onChange={deep.reload} />
      <DeepPages module="recensioni" st={deep.st} unit="recensione" />
      <ReportInline module="recensioni" scope={scope} title="Recensioni" data={rep} generate={generate} />
      <section className="mb-8" data-testid="reviews-list-section">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
          <div className="flex items-center gap-2.5">
            <MessageSquareText size={20} className="text-brand-gold" strokeWidth={1.9} />
            <div>
              <h3 className="font-serif text-[20px] text-brand-ink leading-none">Recensioni e risposte proposte</h3>
              <p className="text-[12.5px] text-brand-muted mt-1">Ogni bozza è scritta nel tono del tuo hotel, senza promesse che non puoi mantenere. Le nuove recensioni sotto le 3 stelle (Google, Booking, Expedia) generano un avviso immediato al direttore, sul telefono e via email.</p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2 text-[12px]">
            <Button data-testid="respond-all-btn" disabled={job?.status === "running"} onClick={respondAll} className="h-9 rounded-lg bg-brand-gold hover:bg-brand-goldSoft text-brand-greenDark text-[12.5px] font-semibold">
              {job?.status === "running" ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Sparkles size={14} className="mr-2" />}
              {job?.status === "running" ? `Scrittura ${job.done}/${job.total}…` : "Una risposta per ogni recensione"}
            </Button>
            <Filter size={14} className="text-brand-muted mr-1" />
            {[["neg", "Negative"], ["unanswered", "Senza risposta"], ["all", "Tutte"]].map(([k, l]) => (
              <button key={k} data-testid={`reviews-filter-${k}`} onClick={() => setFilter(k)}
                className={`px-3 py-1.5 rounded-full border font-semibold transition-colors ${filter === k ? "bg-brand-green text-brand-cream border-brand-green" : "border-brand-gold/30 text-brand-muted hover:text-brand-ink"}`}>{l}</button>
            ))}
          </div>
        </div>
        <div className="space-y-3">
          {list.length === 0 && <Card className="p-6 text-[13px] text-brand-muted italic">Nessuna recensione in questo filtro.</Card>}
          {list.map((r) => {
            const draft = drafts[r.id];
            return (
              <Card key={r.id} className="p-5" data-testid={`review-${r.id}`}>
                <div className="flex flex-wrap items-start gap-4">
                  <div className="flex-1 min-w-[260px]">
                    <div className="flex items-center gap-2 text-[12px] text-brand-muted">
                      <Stars n={r.rating} />
                      <span className="font-semibold text-brand-ink">{r.author}</span>
                      <span>· {r.platform} · {new Date(r.date).toLocaleDateString("it-IT")}</span>
                      <span className="px-2 py-0.5 rounded-full bg-brand-cream border border-brand-gold/30 text-[10.5px] font-bold text-brand-green">{cap(r.theme)}</span>
                      {r.responded && <span className="text-[10.5px] font-bold text-emerald-700">già risposta</span>}
                    </div>
                    <div className="flex gap-2 mt-2"><Quote size={14} className="text-brand-gold shrink-0 mt-1" /><p className="text-[14px] text-brand-ink leading-relaxed">{r.text}</p></div>
                  </div>
                  <div className="w-full lg:w-[440px]">
                    {draft ? <Draft draft={draft} platform={r.platform} publish={data.gbp_publish_enabled && r.source === "gbp" && draft.status !== "published" ? () => publish(r.id, draft.text) : null} markAnswered={!r.responded && !(data.gbp_publish_enabled && r.source === "gbp") ? () => markAnswered(r.id, draft.text, r.platform) : null} /> : (
                      <Button data-testid={`review-reply-btn-${r.id}`} disabled={busy[r.id]} onClick={() => propose(r.id, { review_id: r.id })}
                        className="h-10 rounded-lg bg-brand-green hover:bg-brand-greenDark text-brand-cream text-[13px] font-semibold">
                        {busy[r.id] ? <Loader2 size={15} className="mr-2 animate-spin" /> : <Sparkles size={15} className="mr-2" />}
                        {busy[r.id] ? "Scrittura in corso…" : "Proponi risposta"}
                      </Button>
                    )}
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      </section>
    </div>
  );
}

function Kpi({ label, value, sub, accent, testid }) {
  return (
    <Card className={`p-5 ${accent ? "border-brand-orange/40" : ""}`} data-testid={testid}>
      <div className="text-[11px] font-bold uppercase tracking-wide text-brand-muted">{label}</div>
      <div className={`font-serif text-[34px] leading-none mt-2 ${accent ? "text-brand-orange" : "text-brand-ink"}`}>{value}</div>
      <div className="text-[11.5px] text-brand-muted mt-1">{sub}</div>
    </Card>
  );
}
function Ctx({ label, value }) {
  if (!value) return null;
  return <div className="min-w-[180px] max-w-[300px]"><div className="text-[10.5px] font-bold uppercase tracking-wide text-brand-muted">{label}</div><div className="text-[13px] text-brand-ink mt-0.5 leading-snug">{value}</div></div>;
}
function ChartHead({ title, sub }) {
  return <><div className="text-[13px] font-semibold text-brand-ink">{title}</div><div className="text-[11.5px] text-brand-muted mb-2">{sub}</div></>;
}
function Stars({ n }) {
  return <span className="flex gap-0.5">{[1, 2, 3, 4, 5].map((i) => <Star key={i} size={13} className={i <= n ? "text-brand-gold fill-brand-gold" : "text-stone-300"} />)}</span>;
}
function Draft({ draft, publish, markAnswered, platform }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try { await navigator.clipboard.writeText(draft.text); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch { toast.error("Copia non riuscita"); }
  };
  return (
    <div className="rounded-lg bg-brand-cream border border-brand-gold/30 p-4" data-testid={`draft-${draft.target}`}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-[10.5px] font-bold tracking-[0.16em] text-brand-orange uppercase">Bozza proposta </span>
        <button data-testid={`copy-${draft.target}`} onClick={copy} className="text-[11.5px] font-semibold text-brand-green flex items-center gap-1 hover:text-brand-greenDark">
          {copied ? <Check size={13} /> : <Copy size={13} />} {copied ? "Copiata" : "Copia"}
        </button>
      </div>
      <p className="text-[13px] text-brand-ink leading-relaxed whitespace-pre-line">{draft.text}</p>
      {draft.status === "published" && <div className="mt-2 text-[11px] font-bold text-emerald-700">Pubblicata su Google</div>}
      {draft.status === "answered_manually" && <div className="mt-2 text-[11px] font-bold text-emerald-700" data-testid={`answered-${draft.target}`}>Pubblicata su {platform || "portale"} dal direttore</div>}
      {markAnswered && draft.status !== "answered_manually" && (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <Button data-testid={`copy-mark-${draft.target}`} onClick={async () => { await copy(); }} variant="outline" className="h-9 rounded-lg border-brand-green/30 text-brand-green text-[12.5px] font-semibold"><Copy size={14} className="mr-2" /> Copia per {platform || "il portale"}</Button>
          <Button data-testid={`mark-answered-${draft.target}`} onClick={markAnswered} className="h-9 rounded-lg bg-brand-green hover:bg-brand-greenDark text-brand-cream text-[12.5px] font-semibold"><Check size={14} className="mr-2" /> Ho pubblicato: segna come risposta</Button>
          <span className="text-[11px] text-brand-muted">Incolla la risposta nell'extranet {platform || "del portale"}, poi segna qui.</span>
        </div>
      )}
      {publish && (
        <Button data-testid={`publish-${draft.target}`} onClick={publish} className="mt-3 h-9 rounded-lg bg-brand-green hover:bg-brand-greenDark text-brand-cream text-[12.5px] font-semibold">
          <Check size={14} className="mr-2" /> Pubblica su Google
        </Button>
      )}
    </div>
  );
}

function DeepActionCard({ deep }) {
  const a = deepAction("recensioni", deep, "recensioni");
  return (
    <div className="rounded-xl bg-brand-green text-brand-cream p-5 flex items-center gap-5" data-testid="module-recensioni-action">
      <div className="w-16 h-16 rounded-full border-2 border-brand-gold/70 flex items-center justify-center shrink-0"><a.icon size={28} className="text-brand-gold" strokeWidth={1.5} /></div>
      <div className="flex-1 min-w-0">
        <div className="font-serif text-[22px] leading-tight">{a.title}</div>
        <div className="text-[13px] text-brand-cream/75 mt-1">{a.sub}</div>
        <div className="mt-3">{a.extra}</div>
        <button data-testid="module-recensioni-cta" onClick={a.onClick} disabled={a.busy} className="mt-3 h-10 px-4 rounded-lg bg-brand-gold hover:bg-brand-goldSoft text-brand-greenDark text-[13px] font-semibold inline-flex items-center disabled:opacity-60"><a.ctaIcon size={15} className="mr-2" />{a.cta}</button>
      </div>
    </div>
  );
}
function DeepStatusCard({ deep }) {
  const st = deepStatus(deep, "recensioni");
  return (
    <div className="bg-white rounded-xl border border-brand-gold/25 p-5" data-testid="module-recensioni-status">
      <div className="text-[11px] font-bold uppercase tracking-wide text-brand-muted">Stato analisi recensioni</div>
      <div className="text-[15px] font-semibold mt-2" style={{ color: st.ok ? "#2F6B4F" : "#C2662E" }}>{st.title}</div>
      {st.progress != null && <div className="mt-3 flex items-center gap-3"><div className="flex-1 h-2.5 rounded-full bg-[#EDE7D8] overflow-hidden"><div className="h-full bg-brand-green rounded-full" style={{ width: `${st.progress}%` }} /></div><span className="font-serif text-[20px] text-brand-ink">{st.progress}%</span></div>}
      <div className="mt-2 space-y-0.5">{st.lines.map((l, i) => <div key={i} className="text-[12.5px] text-brand-muted">{l}</div>)}</div>
    </div>
  );
}
